import React from 'react';
import MUIICon from '@mui/material/Icon';
import {useAriaAndDataProps} from '@famly/mf_ts-utils_data-aria-props';
import {useTheme} from '@famly/mf_theme';
import {useTooltipRequiredProps} from '@famly/mf_data-display_tooltip';

import type {IconName} from './icon.type';

export type IconProps = {
    /**
     * An icon from @mui/icons-material or a custom icon wrapped in the SvgIcon component
     */
    name: IconName;
    /**
     * The color of the icon
     */
    color?: ColorKey;
    /**
     * Whether the icon should be filled or not
     */
    filled?: boolean;
    /**
     * The size of the icon.
     */
    size?: number;
};

export const Icon = React.forwardRef<HTMLSpanElement | null, IconProps>(
    ({name, color: colorFromProps, filled, size, ...props}: IconProps, ref) => {
        const theme = useTheme();
        const dataAriaProps = useAriaAndDataProps(props);
        const tooltipRequiredProps = useTooltipRequiredProps(props);

        const color = colorFromProps ? theme.colorPalette[colorFromProps] : 'inherit';

        const sx = {
            fontVariationSettings: filled
                ? `
        'FILL' 1,
        'wght' 300,
        'GRAD' 0,
        'opsz' 24
    `
                : undefined,
            fontSize: size ? `${size}px` : undefined,
            color,
            // Adding these styles to make the icon work better with tooltips
            display: 'inline-flex',
            cursor: 'inherit',
            lineHeight: 1,
        };

        return (
            <MUIICon ref={ref} sx={sx} {...dataAriaProps} {...tooltipRequiredProps}>
                {name}
            </MUIICon>
        );
    },
);

Icon.displayName = 'Icon';
